import * as React from "react";
import { useState } from "react";
import LevelProgressBar from "./level-progress-bar";
import Button from "./button";
import {
  calcLevelByExperience,
  getExperienceToLevel,
} from "@/utils/experience";
import { LevelUpMessage } from "./level-up-message";

export interface ExpGainMessageProps {
  className?: string;
  experience: number;          // gained XP
  previousExperience: number;  // XP before gain
  onClose: () => void;
}


export default function ExpGainMessage({
  className,
  experience,
  previousExperience,
  onClose,
}: ExpGainMessageProps) {
  const [showLevelUpMessage, setShowLevelUpMessage] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<"phase1" | "phase2">(
    "phase1"
  );

  const calculatedCurrentExperience = previousExperience + experience;
  const previousLevel = calcLevelByExperience(previousExperience);
  const currentLevel = calcLevelByExperience(calculatedCurrentExperience);
  const hasLeveledUp = previousLevel < currentLevel;

  const levelThreshold = hasLeveledUp
    ? getExperienceToLevel(previousLevel + 1)
    : getExperienceToLevel(currentLevel + 1);

  const phase1CurrentExp = hasLeveledUp
    ? levelThreshold - 1
    : calculatedCurrentExperience;
  const phase1XpGain = hasLeveledUp ? phase1CurrentExp - previousExperience : 0;

  const phase2CurrentExp = calculatedCurrentExperience;
  const phase2XpGain = experience;

  const displayCurrentExp =
    !hasLeveledUp
      ? calculatedCurrentExperience
      : animationPhase === "phase1"
        ? phase1CurrentExp
        : phase2CurrentExp;

  const displayXpGain =
    experience === 0
      ? 0
      : !hasLeveledUp
        ? experience
        : animationPhase === "phase1"
          ? phase1XpGain
          : phase2XpGain;

  const handleReach100 = () => {
    if (hasLeveledUp && animationPhase === "phase1") {
      setShowLevelUpMessage(true);
    }
  };

  const handleLevelUpMessageClick = () => {
    setShowLevelUpMessage(false);
    setAnimationPhase("phase2");
  };

  return (
    <>
      {/* LEVEL UP MESSAGE POPUP */}
      {showLevelUpMessage && (
        <div className="fixed inset-0 z-50 flex justify-center items-center backdrop-blur-sm bg-black/40">
          <LevelUpMessage
            level={currentLevel}
            onClick={handleLevelUpMessageClick}
          />
        </div>
      )}

      {/* MAIN EXP GAIN WINDOW */}
      <div
        className={`fixed inset-0 z-40 flex justify-center items-center transition-all 
        ${showLevelUpMessage ? "blur-sm pointer-events-none" : "pointer-events-auto"} 
        bg-black/40`}
      >
        <div
          className={`${className} bg-white shadow-xl w-[90%] max-w-[420px]`}
        >
          <header className="relative flex justify-center items-center p-4 bg-[#FE9400] text-white">
            <div className="text-[24px] w-full">
              CONGRATULATION!
            </div>
            <div
              className="absolute right-4 w-[21px] h-[29px] hover:cursor-pointer"
              onClick={onClose}
            >
              <img src="/assets/cancel.svg" alt="cancel" />
            </div>
          </header>

          <main className="flex flex-col justify-center items-center pt-[28px] pb-[20px] px-[40px] text-[20px] bg-black">

            <p className="flex flex-col items-center pt-[20px] pb-[28px] font-spline-sans-mono">
              <span className="text-center">You get {experience} experience!</span>
            </p>
            
            <div className="w-full">
              <LevelProgressBar
                currentExperience={displayCurrentExp}
                hasLeveledUp={hasLeveledUp && animationPhase === "phase2"}
                xpGain={displayXpGain}
                onReach100={handleReach100}
              />
            </div>

            <div className="pt-[40px] w-full">
              <Button
                className="w-full custom-aspect-320-63 text-[22px] hover:cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out"
                image="/assets/button.svg"
                text="OK"
                onClick={onClose}
              />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}